"use client"

import React from "react"
import { cn } from "@/lib/utils"

interface BookingProgressIndicatorProps {
  currentStep: number
  totalSteps: number
  labels: string[]
}

/**
 * BookingProgressIndicator - Visual step indicator
 *
 * Shows breadcrumb-style progress through the booking flow.
 * Completed steps are highlighted, current step is emphasized.
 */
export function BookingProgressIndicator({
  currentStep,
  totalSteps: _totalSteps,
  labels,
}: BookingProgressIndicatorProps) {
  // _totalSteps is kept for API consistency; we derive step count from labels
  return (
    <div className="w-full px-6">
      {/* Circles and connector lines row - aligned at circle centers */}
      <div className="flex items-center w-full">
        {labels.map((label, index) => {
          const stepNumber = index + 1
          const isCompleted = stepNumber < currentStep
          const isCurrent = stepNumber === currentStep
          const isUpcoming = stepNumber > currentStep

          return (
            <React.Fragment key={label}>
              {/* Step circle */}
              <div className="flex-shrink-0">
                <div
                  className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors",
                    isCompleted && "bg-[#848974] text-white",
                    isCurrent && "bg-[#848974] text-white ring-2 ring-[#848974]/30 ring-offset-2",
                    isUpcoming && "bg-gray-200 text-gray-500"
                  )}
                >
                  {isCompleted ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    stepNumber
                  )}
                </div>
              </div>

              {/* Connector line - aligned with circle center */}
              {index < labels.length - 1 && (
                <div
                  className={cn(
                    "flex-1 h-0.5 mx-2 transition-colors",
                    isCompleted ? "bg-[#848974]" : "bg-gray-200"
                  )}
                />
              )}
            </React.Fragment>
          )
        })}
      </div>

      {/* Labels row - positioned below circles */}
      <div className="flex w-full mt-1">
        {labels.map((label, index) => {
          const stepNumber = index + 1
          const isCompleted = stepNumber < currentStep
          const isCurrent = stepNumber === currentStep
          const isUpcoming = stepNumber > currentStep

          return (
            <React.Fragment key={`label-${label}`}>
              {/* Label container - matches circle width */}
              <div className="w-8 flex justify-center flex-shrink-0">
                <span
                  className={cn(
                    "text-xs transition-colors whitespace-nowrap",
                    (isCompleted || isCurrent) && "text-gray-900 font-medium",
                    isUpcoming && "text-gray-400"
                  )}
                >
                  {label}
                </span>
              </div>

              {/* Spacer for connector line - matches connector width */}
              {index < labels.length - 1 && (
                <div className="flex-1 mx-2" />
              )}
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}
